/**
 * The rotation sweep-arc guide (#921), shared by construction's
 * `RotationController` and leather's `TransformController` (#990).
 *
 * A rotation drag reads as a number in the angle chip but gives no sense of the
 * TURN, so the gesture also draws a radial ray from the pivot to the direction
 * currently grabbed, a dashed ghost ray back to the direction the drag started
 * from, and the interior arc between them. It is expressed as a `GuideRay` with
 * a `cornerPrev` — the corner-angle overlay the guide-ray renderer already
 * owns — so it inherits the rays toggle (#745) and the over-leather accent for
 * free.
 *
 * This lives apart from `model/transformHandles` on purpose: that module is
 * where the handles ARE, in world mm, with no rendering concepts in it. This one
 * builds a drawable, so it belongs on the tools side with the other `GuideRay`
 * producers.
 */
import type { GuideRay } from "../canvas/Scene";
import { TRANSFORM_KNOB_OFFSET_MM } from "../model/transformHandles";

/** Below this the drag hasn't visibly turned yet, and the two arms would be a
 *  single stub sticking out of the pivot. Nothing is drawn until it clears. */
export const ROTATION_SWEEP_MIN_DEG = 0.5;

/**
 * The guide for a rotation of `deltaDeg` that began at `startAngleRad` about
 * `pivot`, with the cursor now at `ref`. Returns `null` when the turn is still
 * too small to draw — callers can hand that straight to `Scene.setGuideRays`,
 * which treats `null` as "clear".
 *
 * Both arms are drawn at the cursor's own radius so the arc between them is a
 * clean circle segment rather than a wedge, with a floor of the knob offset so a
 * grab close to the pivot still reads at any zoom.
 */
export function rotationSweepRays(
  pivot: { x: number; y: number },
  startAngleRad: number,
  deltaDeg: number,
  ref: { x: number; y: number },
  zoom: number,
): GuideRay[] | null {
  if (Math.abs(deltaDeg) < ROTATION_SWEEP_MIN_DEG) return null;
  const radius = Math.max(
    Math.hypot(ref.x - pivot.x, ref.y - pivot.y),
    TRANSFORM_KNOB_OFFSET_MM / (zoom > 0 ? zoom : 1),
  );
  const deltaRad = (deltaDeg * Math.PI) / 180;
  const arm = (a: number) => ({
    x: pivot.x + radius * Math.cos(a),
    y: pivot.y + radius * Math.sin(a),
  });
  return [
    {
      from: { x: pivot.x, y: pivot.y },
      to: arm(startAngleRad + deltaRad),
      cornerPrev: arm(startAngleRad),
      cornerPrevGhost: true,
    },
  ];
}
